import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Reads a variant identifier from the freshly rendered product HTML.
 * Shopify's variant JSON payload can omit merchant fields such as SKU and
 * barcode, while the section HTML always contains the Liquid-rendered value.
 *
 * @param {Document | Element | undefined} html
 * @param {string} componentSelector
 * @param {string | undefined} productId
 * @param {string} valueSelector
 * @returns {string | undefined}
 */
function getRenderedIdentifier(html, componentSelector, productId, valueSelector) {
  if (!html || !productId) return undefined;

  const component = Array.from(html.querySelectorAll(componentSelector)).find(
    (candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === productId
  );

  if (!component) return undefined;

  return component.querySelector(valueSelector)?.textContent?.trim() ?? '';
}

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
  requiredRefs = ['skuContainer', 'sku'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the SKU.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { html, newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        const componentSelector = this.closest('.uheat-product-identifiers')
          ? '.uheat-product-identifiers product-sku-component'
          : 'product-sku-component';
        const renderedSku = getRenderedIdentifier(
          html,
          componentSelector,
          this.dataset.productId,
          '[ref="sku"]'
        );
        const variantSku = renderedSku ?? resource?.sku ?? '';

        if (variantSku) {
          // Show the component and update the SKU.
          this.style.display = 'block';
          this.refs.sku.textContent = variantSku;
        } else {
          // Hide the entire component when SKU is empty.
          this.style.display = 'none';
          this.refs.sku.textContent = '';
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-sku] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-sku-component')) {
  customElements.define('product-sku-component', ProductSkuComponent);
}

/**
 * Displays the selected variant barcode as the product EAN.
 * The component listens at document level so it works in both product details
 * and any identifier display rendered outside the main product-information element.
 */
class ProductEanComponent extends HTMLElement {
  connectedCallback() {
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const renderedEan = getRenderedIdentifier(
          detail.html,
          '.uheat-product-identifiers product-ean-component',
          this.dataset.productId,
          '[data-product-ean]'
        );
        const ean = renderedEan ?? detail.resource?.barcode ?? '';
        const value = this.querySelector('[data-product-ean]');

        if (value) value.textContent = ean;
        this.hidden = !ean;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-ean] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-ean-component')) {
  customElements.define('product-ean-component', ProductEanComponent);
}
